import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, EnvelopeSimple, ArrowRight, Stethoscope, CheckCircle, XCircle, Warning, Wrench, DownloadSimple, DeviceMobile } from "@phosphor-icons/react";
import axios from "axios";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useAdminPWA } from "@/lib/useAdminPWA";

const API = process.env.REACT_APP_BACKEND_URL;

function DiagRow({ label, ok }) {
    return (
        <div className="flex items-center justify-between py-2 border-b border-slate-800/60 text-sm">
            <span className="text-slate-300 font-mono-alt">{label}</span>
            {ok ? (
                <span className="flex items-center gap-1.5 text-emerald-400" data-testid={`diag-${label}-ok`}>
                    <CheckCircle size={16} weight="fill" /> ok
                </span>
            ) : (
                <span className="flex items-center gap-1.5 text-rose-400" data-testid={`diag-${label}-fail`}>
                    <XCircle size={16} weight="fill" /> missing
                </span>
            )}
        </div>
    );
}

export default function AdminLogin() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [diag, setDiag] = useState(null);
    const [diagLoading, setDiagLoading] = useState(false);
    const [showReset, setShowReset] = useState(false);
    const [resetSecret, setResetSecret] = useState("");
    const [resetLoading, setResetLoading] = useState(false);
    const [showForgot, setShowForgot] = useState(false);
    const [forgotEmail, setForgotEmail] = useState("");
    const [forgotLoading, setForgotLoading] = useState(false);
    const { user, login } = useAuth();
    const { canInstall, isStandalone, promptInstall } = useAdminPWA();
    const [isIOS, setIsIOS] = useState(false);
    const nav = useNavigate();
    const loc = useLocation();

    useEffect(() => {
        document.title = "Surveys2CAD | Admin Login";
        // Detect iOS Safari (beforeinstallprompt doesn't fire there).
        const ua = window.navigator.userAgent || "";
        setIsIOS(/iPad|iPhone|iPod/.test(ua) && !window.MSStream);
        if (user) {
            nav(loc.state?.from?.pathname || "/admin", { replace: true });
        }
    }, [user, nav, loc]);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, password);
            toast.success("Welcome back.");
            nav(loc.state?.from?.pathname || "/admin", { replace: true });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const status = err?.response?.status;
            if (status === 503 && typeof detail === "string") {
                toast.error(detail);
            } else if (typeof detail === "string") {
                toast.error(detail);
            } else if (status === 500) {
                toast.error("Server error — run diagnostics to see what's missing.");
            } else if (!err?.response) {
                toast.error("Cannot reach backend. Check REACT_APP_BACKEND_URL.");
            } else {
                toast.error("Invalid email or password");
            }
        } finally {
            setLoading(false);
        }
    };

    const runDiagnostics = async () => {
        setDiagLoading(true);
        setDiag(null);
        try {
            const res = await axios.get(`${API}/api/auth/_diag`);
            setDiag(res.data);
        } catch (err) {
            setDiag({
                ok: false,
                network_error: true,
                message: err?.message || "Cannot reach backend. Verify your production REACT_APP_BACKEND_URL and that the backend is deployed.",
            });
        } finally {
            setDiagLoading(false);
        }
    };

    const forceReset = async (e) => {
        e.preventDefault();
        if (!resetSecret.trim()) return;
        setResetLoading(true);
        try {
            const res = await axios.post(`${API}/api/auth/_force_reset_admin`, { jwt_secret: resetSecret.trim() });
            toast.success(res.data?.message || "Admin hash re-seeded. Try signing in now.");
            setShowReset(false);
            setResetSecret("");
            runDiagnostics();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Reset failed.");
        } finally {
            setResetLoading(false);
        }
    };

    const sendForgot = async (e) => {
        e.preventDefault();
        if (!forgotEmail.trim()) return;
        setForgotLoading(true);
        try {
            const res = await axios.post(`${API}/api/auth/forgot-password`, { email: forgotEmail.trim() });
            toast.success(res.data?.message || "If an account with that email exists, a reset link has been sent.");
            setShowForgot(false);
            setForgotEmail("");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Could not send reset email.");
        } finally {
            setForgotLoading(false);
        }
    };

    return (
        <div
            className="min-h-[100dvh] flex items-center justify-center bg-[#030712] p-4 sm:p-6 grid-bg"
            style={{
                paddingTop: "max(1rem, env(safe-area-inset-top))",
                paddingBottom: "max(1rem, env(safe-area-inset-bottom))",
                paddingLeft: "max(1rem, env(safe-area-inset-left))",
                paddingRight: "max(1rem, env(safe-area-inset-right))",
            }}
        >
            <div className="tech-card p-6 sm:p-8 w-full max-w-md" data-testid="admin-login">
                <Logo size="lg" />

                {!isStandalone && (canInstall || isIOS) && (
                    <div className="mt-5 rounded-xl border border-cyan-400/40 bg-cyan-500/10 px-3 py-2.5 flex items-start gap-2.5" data-testid="login-install-cta">
                        <DeviceMobile size={18} weight="fill" className="text-cyan-300 shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                            <div className="text-xs font-semibold text-cyan-100">Install S2C Admin as an app</div>
                            {canInstall ? (
                                <button
                                    onClick={promptInstall}
                                    className="mt-1 text-[11px] text-cyan-300 hover:text-cyan-200 font-mono-alt inline-flex items-center gap-1"
                                    data-testid="login-install-btn"
                                >
                                    <DownloadSimple size={12} weight="bold" /> Tap to install
                                </button>
                            ) : (
                                <p className="text-[11px] text-cyan-200/80 mt-1 leading-snug">
                                    Tap <span className="font-mono-alt">Share ↑</span> in Safari → <span className="font-mono-alt">"Add to Home Screen"</span>.
                                </p>
                            )}
                        </div>
                    </div>
                )}

                <div className="overline mt-6 mb-1 !text-orange-400">Admin access</div>
                <h1 className="font-heading text-3xl font-bold text-slate-50 tracking-tight">Sign in</h1>
                <p className="text-slate-400 text-sm mt-2">Campaigns · Contacts · Templates · Analytics</p>
                <form onSubmit={submit} className="mt-8 space-y-4">
                    <div>
                        <label className="overline mb-2 block">Email</label>
                        <div className="relative">
                            <EnvelopeSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                            <input
                                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                                className="w-full pl-10 bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-cyan-500"
                                data-testid="admin-login-email"
                                placeholder="admin@surveys2cad.com"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="overline mb-2 block">Password</label>
                        <div className="relative">
                            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                            <input
                                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                                className="w-full pl-10 bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-cyan-500"
                                data-testid="admin-login-password"
                            />
                        </div>
                    </div>
                    <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="admin-login-submit">
                        {loading ? "Signing in…" : "Sign in"} <ArrowRight size={14} weight="bold" />
                    </button>
                </form>

                <div className="mt-4 text-right">
                    <button
                        type="button"
                        onClick={() => setShowForgot((v) => !v)}
                        className="text-xs text-cyan-400 hover:text-cyan-300 font-mono-alt"
                        data-testid="forgot-password-toggle"
                    >
                        {showForgot ? "Cancel" : "Forgot password?"}
                    </button>
                </div>
                {showForgot && (
                    <form onSubmit={sendForgot} className="mt-3 space-y-2 bg-slate-900/70 border border-slate-800 rounded-lg p-4" data-testid="forgot-password-form">
                        <p className="text-[11px] text-slate-400 leading-relaxed">
                            Enter your admin email. We'll send a one-time reset link (expires in 30 min) to that address via Gmail SMTP.
                        </p>
                        <input
                            type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                            placeholder="admin@surveys2cad.com"
                            className="w-full bg-slate-950/70 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-100 focus:outline-none focus:border-cyan-500 font-mono-alt"
                            data-testid="forgot-password-email"
                        />
                        <button
                            type="submit" disabled={forgotLoading}
                            className="w-full bg-cyan-500/90 hover:bg-cyan-400 text-slate-950 font-semibold text-xs py-2.5 rounded-lg disabled:opacity-50"
                            data-testid="forgot-password-submit"
                        >
                            {forgotLoading ? "Sending…" : "Send reset link"}
                        </button>
                    </form>
                )}

                <div className="mt-6 pt-5 border-t border-slate-800/60">
                    <button
                        type="button"
                        onClick={runDiagnostics}
                        disabled={diagLoading}
                        className="flex items-center gap-2 text-xs text-cyan-400 hover:text-cyan-300 font-mono-alt uppercase tracking-wider disabled:opacity-50"
                        data-testid="admin-login-diag-button"
                    >
                        <Stethoscope size={14} weight="bold" />
                        {diagLoading ? "Running checks…" : "Run production diagnostics"}
                    </button>

                    {diag && (
                        <div className="mt-4 bg-slate-900/70 border border-slate-800 rounded-lg p-4" data-testid="admin-login-diag-result">
                            {diag.network_error ? (
                                <div className="flex items-start gap-2 text-rose-300 text-sm">
                                    <XCircle size={18} weight="fill" className="shrink-0 mt-0.5" />
                                    <div>
                                        <div className="font-semibold">Backend unreachable</div>
                                        <div className="text-xs text-rose-400/80 mt-1 font-mono-alt">{diag.message}</div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className={`flex items-center gap-2 text-sm font-semibold ${diag.ok ? "text-emerald-400" : "text-amber-400"}`}>
                                        {diag.ok ? <CheckCircle size={18} weight="fill" /> : <Warning size={18} weight="fill" />}
                                        {diag.ok ? "Backend is healthy — login should work." : "Configuration issues detected."}
                                    </div>
                                    <div className="mt-3">
                                        {Object.entries(diag.checks || {}).map(([k, v]) => (
                                            <DiagRow key={k} label={k} ok={v} />
                                        ))}
                                    </div>
                                    {diag.admin_email_in_db && (
                                        <div className="text-xs text-slate-400 mt-3 font-mono-alt">
                                            DB admin: <span className="text-cyan-300">{diag.admin_email_in_db}</span>
                                        </div>
                                    )}
                                    {diag.admin_email_env_masked && (
                                        <div className="text-xs text-slate-400 font-mono-alt">
                                            Env ADMIN_EMAIL: <span className="text-cyan-300">{diag.admin_email_env_masked}</span>
                                        </div>
                                    )}
                                    {diag.env_admin_password_fingerprint && (
                                        <div className="mt-3 bg-slate-950/60 border border-slate-800 rounded-md p-3 text-xs font-mono-alt space-y-1" data-testid="diag-password-fingerprint">
                                            <div className="text-amber-300 font-semibold uppercase tracking-wider text-[10px]">Env ADMIN_PASSWORD fingerprint</div>
                                            <div className="text-slate-300">length: <span className="text-cyan-300">{diag.env_admin_password_fingerprint.length}</span></div>
                                            <div className="text-slate-300">first…last: <span className="text-cyan-300">{diag.env_admin_password_fingerprint.first_last}</span></div>
                                            <div className="text-slate-300">sha256 (first 10): <span className="text-cyan-300">{diag.env_admin_password_fingerprint.sha256_prefix}</span></div>
                                            {diag.env_admin_password_fingerprint.has_trailing_whitespace && (
                                                <div className="text-rose-300">⚠ has leading/trailing whitespace — re-save env var without spaces.</div>
                                            )}
                                            {diag.env_admin_password_fingerprint.has_surrounding_quotes && (
                                                <div className="text-rose-300">⚠ value is wrapped in quotes — remove the quotes in your env var.</div>
                                            )}
                                            <div className="text-slate-500 text-[11px] pt-1 border-t border-slate-800/60">
                                                Verify on your machine: <span className="text-slate-300">echo -n 'YourPassword' | sha256sum | cut -c1-10</span>
                                            </div>
                                        </div>
                                    )}
                                    {Array.isArray(diag.hints) && diag.hints.length > 0 && (
                                        <ul className="mt-3 space-y-1.5 text-xs text-amber-200/90 list-disc list-inside">
                                            {diag.hints.map((h) => (
                                                <li key={h}>{h}</li>
                                            ))}
                                        </ul>
                                    )}

                                    <button
                                        type="button"
                                        onClick={() => setShowReset((v) => !v)}
                                        className="mt-4 flex items-center gap-2 text-xs text-amber-400 hover:text-amber-300 font-mono-alt uppercase tracking-wider"
                                        data-testid="admin-login-reset-toggle"
                                    >
                                        <Wrench size={14} weight="bold" />
                                        {showReset ? "Cancel force reset" : "Force reset admin from env vars"}
                                    </button>
                                    {showReset && (
                                        <form onSubmit={forceReset} className="mt-3 space-y-2" data-testid="admin-login-reset-form">
                                            <p className="text-[11px] text-slate-400 leading-relaxed">
                                                This re-seeds the DB admin password from the current <span className="text-cyan-300 font-mono-alt">ADMIN_PASSWORD</span> env var.
                                                Paste your production <span className="text-cyan-300 font-mono-alt">JWT_SECRET</span> below to authorize.
                                            </p>
                                            <input
                                                type="password"
                                                required
                                                value={resetSecret}
                                                onChange={(e) => setResetSecret(e.target.value)}
                                                placeholder="Paste JWT_SECRET env value"
                                                className="w-full bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-100 focus:outline-none focus:border-amber-500 font-mono-alt"
                                                data-testid="admin-login-reset-secret"
                                            />
                                            <button
                                                type="submit"
                                                disabled={resetLoading}
                                                className="w-full bg-amber-500/90 hover:bg-amber-400 text-slate-950 font-semibold text-xs py-2.5 rounded-lg disabled:opacity-50"
                                                data-testid="admin-login-reset-submit"
                                            >
                                                {resetLoading ? "Re-seeding…" : "Re-seed admin hash now"}
                                            </button>
                                        </form>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>

                <p className="text-xs text-slate-500 mt-6 text-center font-mono-alt">
                    Protected area · HTTPS · HttpOnly cookies
                </p>
            </div>
        </div>
    );
}
